import React from "react";
import ReactDOM from "react-dom";
/** Full screen mode helper */
import { toggleFullScreen } from "core/screen/screenManager.js";
import PopupDICOMTags from "component/molecules/popupDICOMTags";
/** Messagging System */
import pubsub from "global/global";
import CustomEvent from "core/customEvent/index";
/** Tool */
import { getEnabledElementById } from "component/toolManager/enabledElements.js";
import Config from "config";
import { configure } from "react-hotkeys";
/**
 * React-Hotkeys Configuration
 */
configure({
  /**
   * The level of logging of its own behaviour React HotKeys should perform.
   */
  logLevel: "error", // @site : https://www.npmjs.com/package/react-hotkeys#Configuration
  ignoreRepeatedEventsWhenKeyHeldDown: false, // 키가 눌러져 있는 경우, 동일한 이벤트 계속 발생되는 것을 허용
});
/**
 * Copy styles from a source document to a target.
 * @param {Object} source
 * @param {Object} target
 * @private
 */

function copyStyles(source: any, target: any) {
  Array.from(source.styleSheets).forEach((styleSheet: any) => {
    // For <style> elements
    let rules;
    try {
      rules = styleSheet.cssRules;
    } catch (err) {
      console.error(err);
    }
    if (rules) {
      const newStyleEl = source.createElement("style");

      // Write the text of each rule into the body of the style element
      Array.from(styleSheet.cssRules).forEach((cssRule: any) => {
        const { cssText, type } = cssRule;
        let returnText = cssText;
        // Check if the cssRule type is CSSImportRule (3) or CSSFontFaceRule (5) to handle local imports on a about:blank page
        // '/custom.css' turns to 'http://my-site.com/custom.css'
        if ([3, 5].includes(type)) {
          returnText = cssText
            .split("url(")
            .map((line: any) => {
              if (line[1] === "/") {
                return `${line.slice(0, 1)}${
                  window.location.origin
                }${line.slice(1)}`;
              }
              return line;
            })
            .join("url(");
        }
        newStyleEl.appendChild(source.createTextNode(returnText));
      });

      target.head.appendChild(newStyleEl);
    } else if (styleSheet.href) {
      // for <link> elements loading CSS from a URL
      const newLinkEl = source.createElement("link");

      newLinkEl.rel = "stylesheet";
      newLinkEl.href = styleSheet.href;
      target.head.appendChild(newLinkEl);
    }
  });
}

const HotKeyMap = {
  AppConfigGlobalHotKeys: {
    keyMap: {
      // Full screen mode
      FULL_SCREEN_MODE: {
        name: "FULL_SCREEN_MODE",
        description: "Toggle full-screen",
        sequences: ["f11"],
        sequence: "f",
        //sequence: "f",
        action: "keydown",
      },

      // DCMView grid Layout
      LAYOUT_1X1: {
        name: "LAYOUT_1X1",
        description: "Change 1 x 1 layout",
        sequence: "ctrl+alt+1",
        action: "keydown",
      },
      LAYOUT_2X2: {
        name: "LAYOUT_2X2",
        description: "Change 2 x 2 layout",
        sequence: "ctrl+alt+2",
        action: "keydown",
      },
      LAYOUT_3X3: {
        name: "LAYOUT_3X3",
        description: "Change 3 x 3 layout",
        sequence: "ctrl+alt+3",
        action: "keydown",
      },
      LAYOUT_4X4: {
        name: "LAYOUT_4X4",
        description: "Change 4 x 4 layout",
        sequence: "ctrl+alt+4",
        action: "keydown",
      },

      COLLAPSE_COLUMN_LAYOUT: {
        name: "COLLAPSE_COLUMN_LAYOUT",
        description: "Collapse column layout",
        sequence: "ctrl+alt+left",
        action: "keydown",
      },
      EXPAND_COLUMN_LAYOUT: {
        name: "EXPAND_COLUMN_LAYOUT",
        description: "Expand column layout",
        sequence: "ctrl+alt+right",
        action: "keydown",
      },
      COLLAPSE_ROW_LAYOUT: {
        name: "COLLAPSE_ROW_LAYOUT",
        description: "Collapse row layout",
        sequence: "ctrl+alt+up",
        action: "keydown",
      },
      EXPAND_ROW_LAYOUT: {
        name: "EXPAND_ROW_LAYOUT",
        description: "Expand row layout",
        sequence: "ctrl+alt+down",
        action: "keydown",
      },

      // Move slice
      MOVE_BACK_ONE_SLICE: {
        name: "MOVE_BACK_ONE_SLICE",
        description: "Go to previous image",
        sequence: "up",
        action: "keydown",
      },
      MOVE_AHEAD_ONE_SLICE: {
        name: "MOVE_AHEAD_ONE_SLICE",
        description: "Go to next image",
        sequence: "down",
        action: "keydown",
      },
      MOVE_BACK_SLICES: {
        name: "MOVE_BACK_SLICES",
        description: "Go 10 images back",
        sequence: "pageup",
        action: "keydown",
      },
      MOVE_AHEAD_SLICES: {
        name: "MOVE_AHEAD_SLICES",
        description: "Go 10 images forward",
        sequence: "pagedown",
        action: "keydown",
      },

      // Select measurement tool
      SELECT_LENGTH_MEASUREMENT_TOOL: {
        name: "SELECT_LENGTH_MEASUREMENT_TOOL",
        description: "Select length tool",
        sequence: "l",
        action: "keydown",
      },
      SELECT_RECTANGLE_MEASUREMENT_TOOL: {
        name: "SELECT_RECTANGLE_MEASUREMENT_TOOL",
        description: "Select rectangle tool",
        sequence: "r",
        action: "keydown",
      },
      SELECT_ELLIPSE_MEASUREMENT_TOOL: {
        name: "SELECT_ELLIPSE_MEASUREMENT_TOOL",
        description: "Select ellipse tool",
        sequence: "e",
        action: "keydown",
      },
      SELECT_PENCIL_MEASUREMENT_TOOL: {
        name: "SELECT_PENCIL_MEASUREMENT_TOOL",
        description: "Select pen tool",
        sequence: "p",
        action: "keydown",
      },
      SELECT_ANGLE_MEASUREMENT_TOOL: {
        name: "SELECT_ANGLE_MEASUREMENT_TOOL",
        description: "Select angle tool",
        sequence: "a",
        action: "keydown",
      },
      SELECT_COBBANGLE_MEASUREMENT_TOOL: {
        name: "SELECT_COBBANGLE_MEASUREMENT_TOOL",
        description: "Select Cobb angle tool",
        sequence: "b",
        action: "keydown",
      },

      // Select zoom in/out tool
      SELECT_ZOOM_TOOL: {
        name: "SELECT_ZOOM_TOOL",
        description: "Select image zooming tool",
        sequence: "z",
        action: "keydown",
      },
      // Select Stack scroll tool
      SELECT_STACK_SCROLL_TOOL: {
        name: "SELECT_STACK_SCROLL_TOOL",
        description: "Select series browsing tool",
        sequence: "esc",
        action: "keydown",
      },
      // Select pan tool
      SELECT_PAN_TOOL: {
        name: "SELECT_PAN_TOOL",
        description: "Select image panning tool",
        sequence: "m",
        action: "keydown",
      },
      // Initialize zoom/pan state
      INITIALIZE_VIEW_STATE: {
        name: "INITIALIZE_VIEW_STATE",
        description: "Set default Pan/Zoom",
        sequence: "ctrl+0",
        action: "keydown",
      },

      // Select contrast tool
      SELECT_CONTRAST_TOOL: {
        name: "SELECT_CONTRAST_TOOL",
        description:
          //"Select image window (brightness/contrast) adjustment tool",
          "Select window level tool",
        sequence: "c",
        action: "keydown",
      },
      // Change window level by preset
      SELECT_CONTRAST_DEFAULT: {
        name: "SELECT_CONTRAST_DEFAULT",
        description: "Default window",
        sequence: "0",
        action: "keydown",
      },
      SELECT_CONTRAST_FULLDYNAMIC: {
        name: "SELECT_CONTRAST_FULLDYNAMIC",
        description: "Full dynamic",
        sequence: "9",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_LIVER: {
        name: "SELECT_CONTRAST_CT_LIVER",
        description: "Liver CT",
        sequence: "1",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_ABDOMEN: {
        name: "SELECT_CONTRAST_CT_ABDOMEN",
        description: "Abdominal CT",
        sequence: "2",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_ANGIO: {
        name: "SELECT_CONTRAST_CT_ANGIO",
        description: "Angio CT ",
        sequence: "3",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_BONE: {
        name: "SELECT_CONTRAST_CT_BONE",
        description: "Bone CT",
        sequence: "4",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_BRAIN: {
        name: "SELECT_CONTRAST_CT_BRAIN",
        description: "Brain CT",
        sequence: "5",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_CHEST: {
        name: "SELECT_CONTRAST_CT_CHEST",
        description: "Chest CT",
        sequence: "6",
        action: "keydown",
      },
      SELECT_CONTRAST_CT_LUNGS: {
        name: "SELECT_CONTRAST_CT_LUNGS",
        description: "Lung CT",
        sequence: "7",
        action: "keydown",
      },
      SELECT_CONTRAST_NEGATIVE: {
        name: "SELECT_CONTRAST_NEGATIVE",
        description: "Negative",
        sequence: "8",
        action: "keydown",
      },

      // Synchronization
      //ACTIVATE_SYNCRONIZATION: "f8",

      // Info tool
      TOGGLE_ANNOTATION: {
        name: "TOGGLE_ANNOTATION",
        description: "Toggle annotations",
        sequence: "t",
        action: "keydown",
      }, // on/off annotation
      SHOW_DCM_TAG_TABLE: {
        name: "SHOW_DCM_TAG_TABLE",
        description: "Show DICOM tags",
        sequence: "i",
        action: "keydown",
      }, // Show dicom tag table
      SHOW_USER_PREFERENCES: {
        name: "SHOW_USER_PREFERENCES",
        description: "Show User preferences",
        sequence: "u",
        action: "keydown",
      }, // Show dicom tag table
      OPEN_SHORTCUT_PAGE_IN_DOCUMENTATION: {
        name: "OPEN_SHORTCUT_PAGE_IN_DOCUMENTATION",
        description: "Show Keyboard Shortcuts",
        sequence: "s",
        action: "keydown",
      }, // Open shortcut page in documentation
      OPEN_DOCUMENTATION: {
        name: "OPEN_DOCUMENTATION",
        description: "Show documentation",
        sequence: "f1",
        action: "keydown",
      }, // Open documentation
    },
    handlers: {
      // PET Contarst Handler

      // Toggle Fullscreen to window handler
      FULL_SCREEN_MODE: (event: any) => {
        if (event.key == "F11") {
          //(Config.rootStore as any).toolbarStore.toggleFullscreenState();
          return;
        }
        //(Config.rootStore as any).toolbarStore.toggleFullscreen();

        (Config.rootStore as any).toolbarStore.toggleFullscreen();
      },

      // Change Layout 1 x 1
      LAYOUT_1X1: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        const pages = gridLayoutStore.getLayoutTotalPages();
        gridLayoutStore.changeLayout(1, 1, pages);
      },

      // Change Layout 2 x 2
      LAYOUT_2X2: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        const pages = gridLayoutStore.getLayoutTotalPages();
        gridLayoutStore.changeLayout(2, 2, pages);
      },

      // Change Layout 3 x 3
      LAYOUT_3X3: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        const pages = gridLayoutStore.getLayoutTotalPages();
        gridLayoutStore.changeLayout(3, 3, pages);
      },

      // Change Layout 4 x 4
      LAYOUT_4X4: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        const pages = gridLayoutStore.getLayoutTotalPages();
        gridLayoutStore.changeLayout(4, 4, pages);
      },

      COLLAPSE_COLUMN_LAYOUT: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        gridLayoutStore.collapseLayoutToColumn();
      },

      EXPAND_COLUMN_LAYOUT: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        gridLayoutStore.expandLayoutToColumn();
      },

      COLLAPSE_ROW_LAYOUT: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        gridLayoutStore.collapseLayoutToRow();
      },

      EXPAND_ROW_LAYOUT: (event: Event) => {
        const { gridLayoutStore, toolbarStore } = Config.rootStore;
        if (toolbarStore.isBatchViewMode() === true) {
          return;
        }
        gridLayoutStore.expandLayoutToRow();
      },

      ///
      /// Move slice handlers
      ///
      MOVE_BACK_ONE_SLICE: () => {
        pubsub.publish(CustomEvent.Messages.SET_SCROLL_INDEX_ADD, {
          displacement: -1,
        });
      },
      MOVE_AHEAD_ONE_SLICE: () => {
        pubsub.publish(CustomEvent.Messages.SET_SCROLL_INDEX_ADD, {
          displacement: 1,
        });
      },
      MOVE_BACK_SLICES: () => {
        pubsub.publish(CustomEvent.Messages.SET_SCROLL_INDEX_ADD, {
          displacement: -10,
        });
      },
      MOVE_AHEAD_SLICES: () => {
        pubsub.publish(CustomEvent.Messages.SET_SCROLL_INDEX_ADD, {
          displacement: 10,
        });
      },
      ///
      /// Select measurement tool
      ///
      SELECT_LENGTH_MEASUREMENT_TOOL: () => {
        (Config.rootStore as any).toolbarStore.activateMeasureTypeTool(
          "length" /*Length*/
        );
      },
      ///
      /// Select measurement tool
      ///

      SELECT_RECTANGLE_MEASUREMENT_TOOL: () => {
        (Config.rootStore as any).toolbarStore.activateMeasureTypeTool(
          "rectangle" /*Length*/
        );
      },
      SELECT_ELLIPSE_MEASUREMENT_TOOL: () => {
        (Config.rootStore as any).toolbarStore.activateMeasureTypeTool(
          "ellipse" /*Length*/
        );
      },
      SELECT_PENCIL_MEASUREMENT_TOOL: () => {
        (Config.rootStore as any).toolbarStore.activateMeasureTypeTool(
          "pencil" /*Length*/
        );
      },
      SELECT_ANGLE_MEASUREMENT_TOOL: () => {
        (Config.rootStore as any).toolbarStore.activateMeasureTypeTool(
          "angle" /*Length*/
        );
      },
      SELECT_COBBANGLE_MEASUREMENT_TOOL: () => {
        (Config.rootStore as any).toolbarStore.activateMeasureTypeTool(
          "cobb-angle" /*Length*/
        );
      },
      ///
      /// Select stack scroll tool
      ///
      SELECT_STACK_SCROLL_TOOL: () => {
        /// active Pan tool
        (Config.rootStore as any).toolbarStore.setViewportToolType(
          -1 /*Stackscroll*/
        );
      },
      ///
      /// Select zoom in/out tool
      ///
      SELECT_ZOOM_TOOL: () => {
        /// active zoom in/out tool
        (Config.rootStore as any).toolbarStore.setViewportToolType(2 /*Zoom*/);
      },
      ///
      /// Select pan tool
      ///
      SELECT_PAN_TOOL: () => {
        /// active Pan tool
        (Config.rootStore as any).toolbarStore.setViewportToolType(1 /*Pan*/);
      },
      ///
      /// Initialize zoom/pan state
      ///
      INITIALIZE_VIEW_STATE: () => {
        pubsub.publish(CustomEvent.Messages.SET_VIEW_POSITION_INITIALIZE, {});
      },
      ///
      /// Select contrast tool
      ///
      SELECT_CONTRAST_TOOL: () => {
        /// active window level tool
        (Config.rootStore as any).toolbarStore.setViewportToolType(
          3 /*Contrast*/
        );
      },
      /// Change window level by preset
      SELECT_CONTRAST_DEFAULT: () => {
        /// Default
        (pubsub as any).publish(CustomEvent.Messages.CONTRAST_DEFAULT, null);
        (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
          "default-window"
        );
        (Config.rootStore as any).toolbarStore.setViewportToolType(
          3 /*Contrast*/
        );
      },
      SELECT_CONTRAST_FULLDYNAMIC: () => {
        (pubsub as any).publish(
          CustomEvent.Messages.CONTRAST_FULL_DYNAMIC,
          null
        );
        (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
          "full-dynamic"
        );
        (Config.rootStore as any).toolbarStore.setViewportToolType(
          3 /*Contrast*/
        );
      },
      // SELECT_CONTRAST_CT_LIVER: () => {
      //   const payload = {
      //     wl: 100,
      //     ww: 200,
      //     name: "CT Liver",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   const activeDCMViews = (Config.rootStore as any).gridLayoutStore.getActiveDCMViews();
      //   if (activeDCMViews.length === 0) {
      //     return;
      //   }
      //   const activeDCMViewIndex = activeDCMViews[0];
      //   let enabledElement = getEnabledElementById(activeDCMViewIndex);
      //   CustomEvent.triggerEvent(
      //     enabledElement.element,
      //     CustomEvent.EVENTS.WWWC_CHANGED,
      //     enabledElement
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      // SELECT_CONTRAST_CT_ABDOMEN: () => {
      //   const payload = {
      //     wl: 60,
      //     ww: 400,
      //     name: "CT Abdomen",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      // SELECT_CONTRAST_CT_ANGIO: () => {
      //   const payload = {
      //     wl: 300,
      //     ww: 600,
      //     name: "CT Angio",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      // SELECT_CONTRAST_CT_BONE: () => {
      //   const payload = {
      //     wl: 300,
      //     ww: 1500,
      //     name: "CT Bone",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      // SELECT_CONTRAST_CT_BRAIN: () => {
      //   const payload = {
      //     wl: 40,
      //     ww: 80,
      //     name: "CT Brain",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      // SELECT_CONTRAST_CT_CHEST: () => {
      //   const payload = {
      //     wl: 40,
      //     ww: 400,
      //     name: "CT Chest",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      // SELECT_CONTRAST_CT_LUNGS: () => {
      //   const payload = {
      //     wl: -400,
      //     ww: 1500,
      //     name: "CT Lungs",
      //   };
      //   (pubsub as any).publish(
      //     CustomEvent.Messages.CONTRAST_VIEWPORT,
      //     payload
      //   );
      //   (Config.rootStore as any).gridLayoutStore.setContrastOptionNameOnActiveDCMView(
      //     payload.name
      //   );
      //   (Config.rootStore as any).toolbarStore.setViewportToolType(
      //     3 /*Contrast*/
      //   );
      // },
      SELECT_CONTRAST_NEGATIVE: () => {
        pubsub.publish(CustomEvent.Messages.RENDER_INVERT, null);
        (Config.rootStore as any).toolbarStore.setViewportToolType(
          3 /*Contrast*/
        );
        (Config.rootStore as any).gridLayoutStore.toggleNegativeOnActiveDCMView();
      },
      TOGGLE_ANNOTATION: () => {
        (Config.rootStore as any).toolbarStore.toggleShowDCMDescription();
      },
      SHOW_DCM_TAG_TABLE: () => {
        ///
        /// 1. 팝업 창 번호 지정
        ///
        let popup = window.open(
          Config.tagViewer.rootPageURL,
          //popupId,
          "_blank",
          "width=1160,height=593,status=no ,location=no, scrollbars= 0, toolbar=0, menubar=no"
        );
        if (!popup) {
          return;
        }
        const dump = (Config.rootStore as any).gridLayoutStore.getActiveDCMViewDump();
        const patientInfo = (Config.rootStore as any).gridLayoutStore.getActiveDCMViewPatientInfo();

        ///
        /// 2. 팝업 생성시, DICOM Tag 리스트 뷰 렌더링
        ///

        function afterLoadWindow() {
          if (!popup) {
            return;
          }

          ReactDOM.render(
            <PopupDICOMTags
              patientName={patientInfo.patientName}
              modality={patientInfo.modality}
              studyDescription={patientInfo.studyDescription}
              dump={dump}
              windowObject={popup}
            />,
            popup.document.getElementById("root")
          );
          copyStyles(window.document, popup.document);

          popup.removeEventListener("load", afterLoadWindow);
        }
        popup.addEventListener("load", afterLoadWindow, false);
      },
      SHOW_USER_PREFERENCES: () => {
        const { dialogStore } = Config.rootStore;
        dialogStore.setUserPreferencesDialog(true);
      },
      OPEN_SHORTCUT_PAGE_IN_DOCUMENTATION: () => {
        window.open(Config.documentation.shortkeyPageURL, "_blank");
      },
      OPEN_DOCUMENTATION: () => {
        window.open(Config.documentation.rootPageURL, "_blank");
      },
    },
  },
};

export default HotKeyMap;
